import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  NewButton,
  useToast,
} from '@eluve/components';

import {
  getExperimentByIdQuery,
  setExperimentStatusMutation,
} from './operations';

type ExperimentStatusData = {
  id: string;
  isActive: boolean;
  firstActivatedAt: string | null;
};

interface ExperimentStatusToggleProps {
  experimentId: string;
}

export const ExperimentStatusToggle: React.FC<ExperimentStatusToggleProps> = ({
  experimentId,
}) => {
  const toast = useToast();
  const [showWarning, setShowWarning] = useState(false);

  const { data } = useQuery(getExperimentByIdQuery, {
    variables: { experimentId },
  });

  const experiment = data?.experimentsByPk as
    | ExperimentStatusData
    | null
    | undefined;
  const isActive = experiment?.isActive ?? false;
  const isFirstActivation = experiment?.firstActivatedAt === null;

  const [setStatus] = useMutation(setExperimentStatusMutation, {
    optimisticResponse: {
      updateExperimentsByPk: {
        __typename: 'Experiments' as const,
        id: experimentId,
        isActive: !isActive,
        firstActivatedAt:
          isFirstActivation && !isActive
            ? new Date().toISOString()
            : experiment?.firstActivatedAt ?? null,
      },
    },
    onError: () => {
      toast.error('Failed to update experiment status');
    },
  });

  const handleActivate = () => {
    if (isFirstActivation) {
      setShowWarning(true);
    } else {
      setStatus({
        variables: { experimentId, isActive: true },
      });
    }
  };

  const handleDeactivate = () => {
    setStatus({
      variables: { experimentId, isActive: false },
    });
  };

  const handleConfirmActivation = () => {
    setStatus({
      variables: { experimentId, isActive: true },
    });
    setShowWarning(false);
  };

  return (
    <>
      {isActive ? (
        <NewButton
          type="outline"
          text="Deactivate"
          onClick={handleDeactivate}
        />
      ) : (
        <NewButton type="primary" text="Activate" onClick={handleActivate} />
      )}

      <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activate Experiment?</AlertDialogTitle>
            <AlertDialogDescription>
              Once activated, you cannot modify the PostHog flag key or variant
              keys. You also cannot delete the experiment or add/remove
              variants. Are you sure you want to activate this experiment?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmActivation}>
              Activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
